import PropTypes from 'prop-types';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { cloneDeep, map, set, size } from 'lodash';
import { Button, Form, FormGroup } from 'react-bootstrap';
import Pluralize from 'react-pluralize';
import OrbeeTrackingForm from 'components/GroupEditor/Setup/Config/OrbeeTrackingForm';
import feedviewsActions from '../../../redux/feedviews/actions';
import websiteActions from '../../../redux/websites/actions';
import dealersActions from '../../../redux/dealers/actions';
import groupActions from '../../../redux/groups/actions';
import ProdigyInput from '../../GlobalUI/ProdigyInput';
import ProdigyTypeahead from '../../GlobalUI/ProdigyTypeahead';
import GoogleAnalyticsForm from '../../GroupEditor/Setup/Config/GoogleAnalyticsForm';
import LoadingOverlay from '../../LoadingOverlay';
import '../Website.scss';
import { Toggle } from '../../GlobalUI';
import GoogleTagManagerForm from '../../GroupEditor/Setup/Config/GoogleTagManagerForm';
import GoogleAnalyticsV4Form from '../../GroupEditor/Setup/Config/GoogleAnalyticsV4Form';
import AdobeTaggingForm from '../../GroupEditor/Setup/Config/AdobeTaggingForm';

const availableWebsiteDefaultOwnerType = {
  DEALER: 'DEALER',
  DEALER_GROUP: 'DEALER_GROUP',
};

function WebsiteConfig(props) {
  const { website, feedviews, actions, history, dealers, groups, fetching } =
    props;

  const [websiteDefaultOwnerType, setWebsiteDefaultOwnerType] = useState(
    availableWebsiteDefaultOwnerType.DEALER,
  );
  const [availableFeedviews, setAvailableFeedviews] = useState([]);
  const [selectedFeedview, setSelectedFeedview] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [selectedDefaultDealerId, setSelectedDefaultDealerId] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [websiteId, setFeedviewId] = useState(website.id || null);
  const [googleTrackingId, setGoogleTrackingId] = useState();
  const [enableGoogleTracking, setEnableGoogleTracking] = useState();
  const [gaMeasurementIds, setGaMeasurementIds] = useState(['', '', '']);
  const [gtmTrackingId, setGtmTrackingId] = useState();
  const [enableGtmTracking, setEnableGtmTracking] = useState();
  const [enableCustomPickup, setEnableCustomPickup] = useState();
  const [forceLocationSelectFirst, setForceLocationSelectFirst] = useState();
  const [customPickupLocations, setCustomPickupLocations] = useState({});
  const [defaultPickupLocation, setDefaultPickupLocation] = useState(null);
  const [orbeeTrackingEnabled, setOrbeeTrackingEnabled] = useState(false);
  const [orbeeTracking, setOrbeeTracking] = useState({});
  const [enableAdobeTagging, setEnableAdobeTagging] = useState(false);

  const selectedGroup = useMemo(
    () => groups.groups.find((g) => g.id === selectedGroupId) || null,
    [groups.groups, selectedGroupId],
  );

  const selectedDealer = useMemo(
    () => dealers.dealers.find((d) => d.id === selectedDealerId) || null,
    [dealers.dealers, selectedDealerId],
  );

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (website?.id && website?.gaMeasurementIds?.length > 0) {
      const ids = gaMeasurementIds.map(
        (v, index) => website?.gaMeasurementIds?.[index] || v,
      );
      setGaMeasurementIds(ids);
    }
  }, [website]);

  useEffect(() => {
    actions.feedviewsRequest();
    actions.dealersRequest({ addExtraInformation: false });
    actions.groupsRequest();
  }, []);

  useEffect(() => {
    const defaultType = website.dealergroup_id
      ? availableWebsiteDefaultOwnerType.DEALER_GROUP
      : availableWebsiteDefaultOwnerType.DEALER;

    setWebsiteDefaultOwnerType(defaultType);

    if (website.feedview_id) {
      const selectedFeedviewObject = availableFeedviews.filter(
        (fv) => fv.id === website.feedview_id,
      )[0];
      setSelectedFeedview(selectedFeedviewObject);
    }

    if (website.name) {
      setDisplayName(website.name);
    }

    if (website.id) {
      setFeedviewId(website.id);
    }

    if (website.dealer_id) {
      setSelectedDealerId(website.dealer_id);
    }

    if (website.dealergroup_id) {
      setSelectedGroupId(website.dealergroup_id);
    }

    if (website.trackingId) {
      setEnableGoogleTracking(true);
      setGoogleTrackingId(website.trackingId);
    }

    if (website.gtmTrackingId) {
      setEnableGtmTracking(true);
      setGtmTrackingId(website.gtmTrackingId);
    }

    if (website.configs?.adobeTagging?.enabled) {
      setEnableAdobeTagging(true);
    }

    if (website.dealer_id && website.dealergroup_id) {
      setSelectedDefaultDealerId(website.dealer_id);
    }

    if (website.customPickup?.enabled) {
      setEnableCustomPickup(website.customPickup?.enabled);
      setCustomPickupLocations(website.customPickup?.locations || {});
      if (
        website.customPickup?.default?.id &&
        website.customPickup?.locations?.[website.customPickup.default?.id]
      ) {
        setDefaultPickupLocation(website.customPickup.default);
      }

      if (website.customPickup?.forceLocationSelectFirst?.enabled) {
        setForceLocationSelectFirst(
          website.customPickup?.forceLocationSelectFirst?.enabled,
        );
      }
    }

    if (website.configs?.orbeeTracking) {
      setOrbeeTracking(website.configs?.orbeeTracking);
    }
  }, [website]);

  useEffect(() => {
    const groupId = website.DealerGroup?.id;
    actions.resetGroup();
    if (groupId) {
      setCustomPickupLocations(website.customPickup?.locations || {});
      if (
        website.customPickup?.default?.id &&
        website.customPickup?.locations?.[website.customPickup.default?.id]
      ) {
        setDefaultPickupLocation(website.customPickup.default);
      }
      actions.groupRequest({
        groupId,
      });
    }
  }, [enableCustomPickup]);

  useEffect(() => {
    setAvailableFeedviews(feedviews.feedviews);
    if (website?.feedview_id) {
      const selectedFeedviewObject = feedviews.feedviews.filter(
        (fv) => fv.id === website.feedview_id,
      )[0];
      setSelectedFeedview(selectedFeedviewObject);
    }
  }, [feedviews]);

  const onToggleGoogleAnalytics = useCallback(() => {
    if (enableGoogleTracking) {
      setGoogleTrackingId(null);
    }
    setEnableGoogleTracking(!enableGoogleTracking);
  }, [enableGoogleTracking]);

  const onToggleGoogleTagManagerAnalytics = useCallback(() => {
    if (enableGtmTracking) {
      setGtmTrackingId(null);
    }
    setEnableGtmTracking(!enableGtmTracking);
  }, [enableGtmTracking]);

  const onToggleAdobeTagging = useCallback(() => {
    setEnableAdobeTagging(!enableAdobeTagging);
  }, [enableAdobeTagging]);

  const onChangeGoogleTrackingId = useCallback((e) => {
    setGoogleTrackingId(e.target.value);
  }, []);

  const onChangeGoogleTagManagerTrackingId = useCallback((e) => {
    setGtmTrackingId(e.target.value);
  }, []);

  const onToggleEnableCustomPickup = useCallback(() => {
    setEnableCustomPickup(!enableCustomPickup);
  }, [enableCustomPickup]);

  const onToggleForceLocationSelectFirst = useCallback(() => {
    setForceLocationSelectFirst(!forceLocationSelectFirst);
  }, [forceLocationSelectFirst]);

  const onChangeCustomPickupLocation = (id, val) => {
    const pickupLocationsClone = cloneDeep(customPickupLocations);
    if (pickupLocationsClone[id]) {
      delete pickupLocationsClone[id];
    } else {
      pickupLocationsClone[id] = val;
    }
    setCustomPickupLocations(pickupLocationsClone);
    if (
      defaultPickupLocation &&
      !pickupLocationsClone?.[defaultPickupLocation?.id]
    ) {
      setDefaultPickupLocation(null);
    }
  };

  const renderDealerRow = (dealer) => {
    // Is this Dealer set as the Online default?
    const isDefault =
      !selectedDefaultDealerId ||
      (selectedDefaultDealerId && selectedDefaultDealerId !== dealer.id);
    return (
      <div className="feed-row">
        <div className="feed-name">{dealer.name}</div>
        <div className="feed-action">
          <button
            className={!isDefault ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={(e) => {
              setSelectedDefaultDealerId(isDefault ? dealer.id : null);
              e.preventDefault();
            }}
          >
            {isDefault ? 'Make Online Default' : 'Remove Online Default'}
          </button>
        </div>
      </div>
    );
  };

  const handleWebsiteOwnerChange = (changeEvent) => {
    setWebsiteDefaultOwnerType(changeEvent.target.value);
  };

  const isChecked = (value) => websiteDefaultOwnerType === value;

  const validateForm = () => {
    const e = {};
    if (websiteId === null) {
      e.websiteId = {
        msg: 'Website Id is Required',
      };
      setErrors(e);
      return false;
    }

    if (displayName === '') {
      e.displayName = {
        msg: 'Website Name is Required',
      };
      setErrors(e);
      return false;
    }

    if (!selectedFeedview) {
      e.feedview = {
        msg: 'Feedview is Required',
      };
      setErrors(e);
      return false;
    }

    if (
      !selectedDealerId &&
      websiteDefaultOwnerType === availableWebsiteDefaultOwnerType.DEALER
    ) {
      e.Dealer = {
        msg: 'Dealer is Required',
      };
      setErrors(e);
      return false;
    }

    if (
      !selectedGroupId &&
      websiteDefaultOwnerType === availableWebsiteDefaultOwnerType.DEALER_GROUP
    ) {
      e.DealerGroup = {
        msg: 'Dealer Group is Required',
      };
      setErrors(e);
      return false;
    }

    if (
      !selectedDefaultDealerId &&
      websiteDefaultOwnerType === availableWebsiteDefaultOwnerType.DEALER_GROUP
    ) {
      e.DealerDefault = {
        msg: 'Default online website needs to be selected',
      };
      setErrors(e);
      return false;
    }

    if (enableCustomPickup && !defaultPickupLocation) {
      e.DeafaultPickup = {
        msg: 'Default pickup location needs to be selected',
      };
      setErrors(e);
      return false;
    }

    if (
      orbeeTrackingEnabled &&
      (!orbeeTracking?.dealerDomain || !orbeeTracking?.providerToken)
    ) {
      e.orbeeTracking = {
        msg: 'Orbee Tracking needs dealer domain and provider token',
      };
      setErrors(e);
      return false;
    }

    if (gaMeasurementIds.length) {
      const measurementIdRegex = /^G-[0-9A-Z]{7,11}$/;
      const messages = gaMeasurementIds.map((id) => {
        if (id === '' || measurementIdRegex.test(id)) {
          return '';
        }
        return 'Invalid measurement id';
      });

      if (messages.some((m) => m !== '')) {
        e.measurementIds = messages;
        setErrors(e);
        return false;
      }
    }

    setErrors(e);
    return true;
  };

  const cancel = () => {
    history.push('/websites/');
  };

  const getWebsiteForRequest = () => {
    const cloneWebsite = cloneDeep(website);
    if (
      selectedDealerId &&
      websiteDefaultOwnerType === availableWebsiteDefaultOwnerType.DEALER
    ) {
      // WEBSITE DEFAULT OWNER IS DEALER
      cloneWebsite.dealer_id = selectedDealerId;
      cloneWebsite.dealergroup_id = selectedGroupId;
    }

    if (
      selectedGroupId &&
      websiteDefaultOwnerType === availableWebsiteDefaultOwnerType.DEALER_GROUP
    ) {
      // WEBSITE DEFAULT OWNER IS DEALER GROUP
      cloneWebsite.dealergroup_id = selectedGroupId;
      cloneWebsite.dealer_id = null;
      //
      if (selectedDefaultDealerId) {
        cloneWebsite.dealer_id = selectedDefaultDealerId;
      }
    }
    cloneWebsite.trackingId = enableGoogleTracking
      ? googleTrackingId || null
      : null;
    cloneWebsite.gaMeasurementIds = gaMeasurementIds?.filter((id) => id !== '');
    setGaMeasurementIds(cloneWebsite.gaMeasurementIds);

    cloneWebsite.gtmTrackingId = enableGtmTracking
      ? gtmTrackingId || null
      : null;
    set(
      cloneWebsite,
      'configs.adobeTagging.enabled',
      enableAdobeTagging || false,
    );
    cloneWebsite.feedview_id = selectedFeedview?.id
      ? selectedFeedview.id
      : null;
    cloneWebsite.name = displayName;
    cloneWebsite.id = websiteId;
    cloneWebsite.widgetConfig = website.widgetConfig;
    set(cloneWebsite, 'customPickup.enabled', enableCustomPickup);
    if (enableCustomPickup) {
      set(cloneWebsite, 'customPickup.locations', customPickupLocations);
      set(cloneWebsite, 'customPickup.default', defaultPickupLocation);
    }
    set(
      cloneWebsite,
      'customPickup.forceLocationSelectFirst.enabled',
      forceLocationSelectFirst,
    );
    set(cloneWebsite, 'configs.orbeeTracking', orbeeTracking);
    delete cloneWebsite.DealerGroup;
    delete cloneWebsite.Dealer;
    return cloneWebsite;
  };

  const submitForm = () => {
    const newWebsite = getWebsiteForRequest();

    if (validateForm()) {
      if (!website.id && websiteId !== null) {
        actions.createWebsiteRequest({ ...newWebsite });
      } else {
        actions.updateWebsiteRequest({ website: newWebsite });
      }
    }
  };
  const getFullAddress = ({ address, city, state }) => {
    const value = [];
    if (address) {
      value.push(address);
      if (city) {
        value.push(city);
      }
      if (state) {
        value.push(state);
      }
    }
    return value.join(', ');
  };

  const setWebsiteId = (str) => {
    const e = {};
    // TODO: temporary relax websiteId naming restrictions
    // uncomment when revert https://upstartnetwork.atlassian.net/browse/REL-14745
    // str = str.toLowerCase();
    if (/[^A-Za-z0-9-?]/g.test(str)) {
      e.websiteId = {
        msg: 'Only lowercase letters, numbers and dashes are allowed.',
      };
      setErrors(e);
    } else {
      setErrors(e);
    }
    str = str.replace(/[^A-Za-z0-9-?]/g, '');
    setFeedviewId(str);
  };

  return (
    <div className="website-tab-container">
      <div className="tab-main-content-container">
        <LoadingOverlay active={fetching}>
          <Form>
            <FormGroup>
              <div className="row">
                <div className="field col-sm-4">
                  <ProdigyInput
                    value={websiteId || ''}
                    label="ID"
                    onChangeHandler={setWebsiteId}
                    required
                    error={errors.websiteId}
                    disabled={website.id}
                  />
                </div>
                <div className="field col-sm-4">
                  <ProdigyInput
                    value={displayName}
                    label="Name"
                    onChangeHandler={setDisplayName}
                    required
                    error={errors.displayName}
                  />
                </div>
                <div className="field col-sm-4">
                  {availableFeedviews && availableFeedviews.length > 0 && (
                    <ProdigyTypeahead
                      id="feedview-typeahead"
                      placeholder="Add a Feedview"
                      className="feedview-typeahead"
                      selectHintOnEnter
                      clearButton
                      filterBy={['label']}
                      label="Feedview"
                      value={{ ...selectedFeedview }}
                      options={availableFeedviews}
                      onChange={(fv) => setSelectedFeedview(fv[0])}
                      error={errors.feedview}
                      required
                    />
                  )}
                </div>
              </div>

              <div className="row">
                <div className="field col-sm-6 add-feed-input-container">
                  <div className="protection-plans-source-input-container">
                    Website Owner:
                    <label>
                      <input
                        type="radio"
                        name="websiteOwner"
                        value={availableWebsiteDefaultOwnerType.DEALER}
                        checked={isChecked(
                          availableWebsiteDefaultOwnerType.DEALER,
                        )}
                        onChange={handleWebsiteOwnerChange}
                        className="form-check-input"
                      />
                      Dealer
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="websiteOwner"
                        value={availableWebsiteDefaultOwnerType.DEALER_GROUP}
                        checked={isChecked(
                          availableWebsiteDefaultOwnerType.DEALER_GROUP,
                        )}
                        onChange={handleWebsiteOwnerChange}
                        className="form-check-input"
                      />
                      Dealer Group
                    </label>
                  </div>
                </div>
              </div>

              {isChecked(availableWebsiteDefaultOwnerType.DEALER) && (
                <div className="row">
                  <div className="field col-sm-6 typeahead-input-container">
                    {dealers.dealers && (
                      <ProdigyTypeahead
                        id="dealers-typeahead"
                        placeholder="Select a Dealer"
                        selectHintOnEnter
                        clearButton
                        filterBy={['id']}
                        labelKey="id"
                        label="Dealer"
                        value={selectedDealer}
                        options={dealers.dealers}
                        onChange={(dealer) =>
                          setSelectedDealerId(dealer[0]?.id || null)
                        }
                        error={errors.Dealer}
                        required
                      />
                    )}
                  </div>
                </div>
              )}

              {isChecked(availableWebsiteDefaultOwnerType.DEALER_GROUP) && (
                <div className="row">
                  <div className="field col-sm-6 typeahead-input-container">
                    {groups.groups && (
                      <ProdigyTypeahead
                        id="groups-typeahead"
                        placeholder="Select a Dealer Group"
                        selectHintOnEnter
                        clearButton
                        filterBy={['id']}
                        labelKey="name"
                        value={selectedGroup}
                        label="Dealer Group"
                        options={groups.groups}
                        onChange={(group) =>
                          setSelectedGroupId(group[0]?.id || null)
                        }
                        error={errors.DealerGroup}
                        required
                      />
                    )}
                  </div>
                  {selectedGroupId && (
                    <div className="field col-sm-6">
                      <label>
                        <Pluralize
                          singular={`Dealer in ${selectedGroup.name}`}
                          plural={`Dealers in ${selectedGroup.name}`}
                          count={selectedGroup.Dealers?.length}
                        />
                      </label>

                      <div className="prodigy-input-container-error">
                        <p className="prodigy-input-error">
                          {errors.DealerDefault?.msg}
                        </p>
                      </div>

                      <div className="website-dealers-container">
                        <div className="feed-header-row">
                          <div>Name</div>
                          <div className="feed-action">Actions</div>
                        </div>
                        {selectedGroup && (
                          <div>
                            {selectedGroup.Dealers?.map(renderDealerRow)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="row">
                <div className="field col-sm-4">
                  <GoogleAnalyticsForm
                    enabled={enableGoogleTracking}
                    onToggle={onToggleGoogleAnalytics}
                    trackingId={googleTrackingId}
                    onChangeGoogleId={onChangeGoogleTrackingId}
                  />
                </div>
                <div className="field col-sm-4">
                  <GoogleAnalyticsV4Form
                    gaMeasurementIds={gaMeasurementIds}
                    setGaMeasurementIds={setGaMeasurementIds}
                    errors={errors?.measurementIds}
                  />
                </div>
                <div className="field col-sm-4">
                  <GoogleTagManagerForm
                    enabled={enableGtmTracking}
                    onToggle={onToggleGoogleTagManagerAnalytics}
                    gtmTrackingId={gtmTrackingId}
                    onChangeGoogleTagManagerId={
                      onChangeGoogleTagManagerTrackingId
                    }
                  />
                </div>
                <div className="field col-sm-4">
                  <AdobeTaggingForm
                    enabled={enableAdobeTagging}
                    onToggle={onToggleAdobeTagging}
                  />
                </div>
                <div className="field col-sm-4">
                  <OrbeeTrackingForm
                    orbeeTracking={orbeeTracking}
                    setOrbeeTracking={setOrbeeTracking}
                    enabled={orbeeTrackingEnabled}
                    setEnabled={setOrbeeTrackingEnabled}
                    errorMessage={errors?.orbeeTracking?.msg}
                  />
                </div>
              </div>
              <div className="row">
                {website.dealergroup_id && (
                  <>
                    <div className="field col-sm-8">
                      <div className="setup-config-section">
                        <div className="setup-config-header">Custom Pickup</div>
                        <div className="setup-config-content">
                          <b>Enable Custom Pickup </b>
                          <Toggle
                            value={enableCustomPickup}
                            onToggle={onToggleEnableCustomPickup}
                          />
                        </div>
                        {!!enableCustomPickup && (
                          <div className="setup-config-content">
                            <h3>Locations</h3>
                            <div className="custom-pick-up-locations">
                              {map(
                                groups?.group?.Dealers,
                                ({ address, city, id, name, state, zip }) => {
                                  const fullAddress = getFullAddress({
                                    address,
                                    city,
                                    state,
                                  });
                                  return (
                                    <label
                                      htmlFor={`customPickupLocation-${id}`}
                                      key={`${id}-${!!customPickupLocations?.[
                                        id
                                      ]}`}
                                    >
                                      <input
                                        type="checkbox"
                                        id={`customPickupLocation-${id}`}
                                        name={`customPickupLocation-${id}`}
                                        value={customPickupLocations?.[id]}
                                        checked={customPickupLocations?.[id]}
                                        onChange={() =>
                                          onChangeCustomPickupLocation(id, {
                                            address: fullAddress,
                                            zip,
                                            name,
                                            id,
                                          })
                                        }
                                        className="form-check-input"
                                      />
                                      {`${name} - ${fullAddress}, ${zip}`}
                                    </label>
                                  );
                                },
                              )}
                            </div>
                            <div>
                              {size(customPickupLocations) > 0 && (
                                <ProdigyTypeahead
                                  id="pickup-typeahead"
                                  placeholder="Select a Default Pickup Location"
                                  selectHintOnEnter
                                  clearButton
                                  filterBy={['id']}
                                  labelKey="name"
                                  value={defaultPickupLocation}
                                  label="Default Pickup Location"
                                  options={map(
                                    customPickupLocations,
                                    (val) => val,
                                  )}
                                  onChange={(locations) => {
                                    setDefaultPickupLocation(locations[0]);
                                  }}
                                  error={errors.DealerGroup}
                                  required
                                />
                              )}
                              <div className="prodigy-input-container-error">
                                <p className="prodigy-input-error">
                                  {errors.DeafaultPickup?.msg}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {!!enableCustomPickup && (
                      <div className="field col-sm-4">
                        <div className="setup-config-section">
                          <div className="setup-config-header">
                            Force location select on 1st Load
                          </div>
                          <div className="setup-config-content">
                            <b>Enabled</b>
                            <Toggle
                              value={forceLocationSelectFirst}
                              onToggle={onToggleForceLocationSelectFirst}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </FormGroup>
          </Form>
        </LoadingOverlay>
      </div>
      <div className="row submit-button-row">
        <div>
          <Button variant="secondary" type="button" onClick={() => cancel()}>
            Cancel
          </Button>
        </div>
        <div>
          <Button
            className="btn btn-primary"
            variant="primary"
            type="button"
            onClick={() => submitForm()}
          >
            Save Config
          </Button>
        </div>
      </div>
    </div>
  );
}

WebsiteConfig.propTypes = {
  website: PropTypes.object,
  feedviews: PropTypes.object,
  actions: PropTypes.object,
  history: PropTypes.object,
  dealers: PropTypes.object,
  groups: PropTypes.object,
  fetching: PropTypes.bool,
};

const mapStateToProps = ({ feedviews, dealers, groups, websites }) => ({
  feedviews,
  dealers,
  groups,
  fetching: websites.fetching,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...feedviewsActions,
      ...websiteActions,
      ...dealersActions,
      ...groupActions,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(WebsiteConfig);
